import * as React from "react";

import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import {
    DEFAULT_CONTENT_LAYOUT,
    DEFAULT_CONTENT_WIDTH_IN_PX,
    PageActionsContainer,
    UnitContentInner,
    UnitsLayoutItem,
} from "../Navigation/UnitsLayout/UnitsLayout";
import { arrayHasLength, noop } from "../../../../../Common/TypingUtils";
import { ScrollableContainer2 } from "../../Internals/ScrollableContainer2/ScrollableContainer2";
import {
    ADD_PAGE,
    ADD_XML_PAGE_MODAL_OPEN,
    CLEAR_PAGE,
    CLEAR_PAGE_MODAL_OPEN,
    ENABLE_AUTO_VALUES_PAGE,
    REMOVE_PAGE,
    REMOVE_PAGE_MODAL_OPEN,
} from "../../metricsAction";
import { IUnitController, IUnitRegistry } from "../Navigation/UnitRegistry/IUnitRegistry";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { usePartialRender } from "../Navigation/UnitPartialRendering/PartiallyRenderedUnit";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { ClearPageMethod } from "../../../Engine/ActionCreators/UnitActionCreators";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";

import { UnitId, unitIdToLegacyUnitId } from "./UnitId/UnitId";
import { IsFilledContext } from "./Internals/OptionalPages/IsFilledContext";
import { UnitIdProvider } from "./UnitIdContext";
import { mapPageActionsByProps } from "./Internals/mapPageActions";
import { ResolveOptionalPage } from "./Internals/OptionalPages/ResolveOptionalPage";
import { PageAction, PageActions } from "./Internals/PageActions";
import { AddSingleUnitContextProvider } from "./AddSingleUnitContext";
import { UnitLoader } from "./Internals/UnitLoader/UnitLoader";
import { useUnitLayoutingContext } from "./Internals/UnitBorderContext/UnitLayoutingContext";
import { UnitRemoveConfirmModal } from "./Modal/UnitRemoveConfirmModal";
import { UnitClearConfirmModal } from "./Modal/UnitClearConfirmModal";
import { UnitLayoutKind } from "./UnitLayoutKind";
import { ChildPage, UnitClearConfirmationSettings } from "./Unit";
import { UnitIdImmutableSet } from "./UnitId/UnitIdImmutableSet";
import { PropsPageActions } from "./Types";

export interface SingleUnitProps {
    viewModelSelect: ViewModelSelect;
    noContent: boolean;
    unitId: UnitId;
    name?: string;
    bindingPath?: AbsoluteModelPath;
    unitRegistry: IUnitRegistry;
    children?: React.ReactNode;
    layoutKind: UnitLayoutKind;
    height?: number;
    hidden?: boolean;
    minHeight?: number;
    maxWidth?: number;
    optional?: boolean;
    pages: ChildPage[];
    clearConfirmationSettings: UnitClearConfirmationSettings;
    pageActions: PropsPageActions;
    hideEnableAutoValues?: boolean;
    enableHorizontalScrolling?: boolean;
    onHighlightUnit: (unitId: UnitId) => void;
    onEnableAutoValues?: () => void;
    unitsWidth: { [key: string]: { width: number } };
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    onAddPage: () => void;
    onRemovePage: () => void;
    onClearPage?: (method: ClearPageMethod) => void;
    onMount: () => void;
    onUnmount: () => void;
    onRemoveAttachment?: () => void;
    enableUploadFilesInPage: boolean;
    uploadFilesInPage?: () => void;
    showUnitBorder: boolean;
    isReadOnly?: boolean;
    emptyUnits: UnitIdImmutableSet;
    offPartialRender?: boolean;
    extraPageActions?: PageAction[];
    hasAttachmentActions?: boolean;
}

export function SingleUnit(props: SingleUnitProps): JSX.Element {
    const track = useMetrics();
    const [loaded, partialLoadRef] = usePartialRender();
    const unit = React.useRef<HTMLDivElement | null>(null);
    const [unitContent, ...nestedUnits] = React.Children.toArray(props.children);
    const hasChildPages = props.pages.length > 0;
    const { pageActions, optional, unitId, unitRegistry } = props;
    const legacyUnitId = React.useMemo(() => unitIdToLegacyUnitId(unitId), [unitId]);
    const { marginTop, unitContextRef } = useUnitLayoutingContext();
    const [submitRemoveModalOpened, setSubmitRemoveModalOpened] = React.useState(false);
    const [submitClearModalOpened, setSubmitClearModalOpened] = React.useState(false);

    const unitRef = (element: HTMLDivElement | null) => {
        unit.current = element;
        if (unitContextRef != undefined) {
            unitContextRef.current = element;
        }
        partialLoadRef(element);
    };

    const handleFocusRequest = (): void => {
        if (unit.current != undefined) {
            tryScrollIntoViewWithFallback(unit.current, { block: "center", inline: "center" });
        }
    };
    const controller = React.useMemo<IUnitController>(
        () => ({
            getRootElement: function (): HTMLElement | undefined {
                return unit.current ?? undefined;
            },
            ensureUnitAvailableForScroll: noop,
            scrollIntoView: function () {
                unit.current?.scrollIntoView();
            },
        }),
        []
    );

    React.useEffect(() => {
        props.onMount();

        return () => {
            props.onUnmount();
        };
    }, []);

    React.useEffect(() => {
        unitRegistry.registerUnit(unitId, controller);
        return () => unitRegistry.unregisterUnit(unitId);
    }, [unitId]);

    const handleSingleUnitMouseEnter = React.useCallback(() => {
        props.onHighlightUnit(unitId);
    }, [unitId]);

    const addPage = React.useCallback((): void => {
        track(ADD_PAGE, { page: legacyUnitId });
        props.onAddPage();
    }, [legacyUnitId]);

    const removePage = React.useCallback((): void => {
        setSubmitRemoveModalOpened(true);
        track(REMOVE_PAGE_MODAL_OPEN, { page: legacyUnitId });
    }, [legacyUnitId]);

    const addXml = React.useCallback((): void => {
        props.enableUploadFilesInPage && props.uploadFilesInPage?.();
        track(ADD_XML_PAGE_MODAL_OPEN, { page: legacyUnitId });
    }, [legacyUnitId]);

    const handleRemovePage = () => {
        track(REMOVE_PAGE, { page: legacyUnitId });
        setSubmitRemoveModalOpened(false);
        props.onRemovePage();
    };

    const clearPage = React.useCallback((): void => {
        setSubmitClearModalOpened(true);
        track(CLEAR_PAGE_MODAL_OPEN, { page: legacyUnitId });
    }, [legacyUnitId]);

    const handleClearPage = (method: ClearPageMethod) => {
        track(CLEAR_PAGE, { page: legacyUnitId, method: method });
        setSubmitClearModalOpened(false);
        props.onClearPage?.(method);
    };

    const handleEnableAutoValues = React.useCallback((): void => {
        track(ENABLE_AUTO_VALUES_PAGE, { pageId: legacyUnitId });
        if (props.onEnableAutoValues != undefined) {
            props.onEnableAutoValues();
        }
    }, []);

    const defaultPageActions = React.useMemo(() => {
        const extraActions: { [name: string]: PageAction } =
            props.onEnableAutoValues != undefined && !props.hideEnableAutoValues
                ? {
                      enableAutoValues: {
                          "data-tid": `EnableAutoValues`,
                          description: "Включить авторасчет",
                          handler: handleEnableAutoValues,
                          disabled: false,
                      },
                  }
                : {};

        if (props.onRemoveAttachment) {
            extraActions["removeAttachment"] = {
                "data-tid": `RemoveAttachment`,
                description: "Удалить раздел",
                handler: props.onRemoveAttachment,
                disabled: false,
            };
        }

        if (props.enableUploadFilesInPage) {
            extraActions["addXml"] = {
                "data-tid": `AddXml`,
                description: "Загрузить из файла",
                handler: addXml,
                disabled: false,
            };
        }

        if (props.onClearPage) {
            extraActions["clear"] = {
                "data-tid": `Clear`,
                description: "Очистить раздел",
                handler: clearPage,
                disabled: false,
            };
        }

        return {
            ...extraActions,
            remove: {
                "data-tid": `Remove`,
                description: "Удалить раздел",
                handler: removePage,
                disabled: false,
            },
        };
    }, [clearPage, removePage]);

    const mappedActions = React.useMemo(() => {
        const actions = mapPageActionsByProps(pageActions.actions, defaultPageActions).filter(action =>
            optional ? action : action["data-tid"] !== "Remove"
        );

        if (props.extraPageActions?.length) {
            return actions.concat(props.extraPageActions);
        }

        return actions;
    }, [optional, defaultPageActions]);

    const renderSingleUnitContent = (children: React.ReactNode): JSX.Element => {
        const pageActionsDisplayed =
            (mappedActions.length > 0 && !/title/i.test(unitId.pageId) && !pageActions.hideButton) ||
            props.hasAttachmentActions;
        const pageActionsDisabled =
            pageActions.disableButton || (arrayHasLength(mappedActions, 1) && mappedActions[0].disabled);

        const content = (
            <UnitContentInner>
                <IsFilledContext.Consumer>
                    {isFilled =>
                        pageActionsDisplayed &&
                        isFilled &&
                        !props.isReadOnly && (
                            <PageActionsContainer>
                                <PageActions
                                    caption={pageActions.caption || "Действия с разделом"}
                                    disabled={pageActionsDisabled}
                                    actions={mappedActions}
                                />
                            </PageActionsContainer>
                        )
                    }
                </IsFilledContext.Consumer>
                {children}
            </UnitContentInner>
        );
        if (props.enableHorizontalScrolling) {
            return <ScrollableContainer2>{content}</ScrollableContainer2>;
        }
        return content;
    };

    const testPageObject = React.useMemo(
        () => ({
            getType: () => "SingleUnit",
            addPage: addPage,
            removePage: removePage,
        }),
        [addPage, removePage]
    );

    if (props.noContent) {
        return <>{nestedUnits}</>;
    }

    return (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            <TestPageObjectHandle id={[props.bindingPath, props.unitId.pageId]} pageObject={testPageObject}>
                <AddSingleUnitContextProvider value={addPage}>
                    <UnitIdProvider id={props.unitId}>
                        {submitRemoveModalOpened && (
                            <UnitRemoveConfirmModal
                                name={props.name}
                                hasChildren={hasChildPages}
                                removeChildrenText={props.clearConfirmationSettings.removeChildrenText}
                                onRemove={handleRemovePage}
                                onClose={() => setSubmitRemoveModalOpened(false)}
                            />
                        )}
                        {submitClearModalOpened && (
                            <UnitClearConfirmModal
                                name={props.name}
                                hasChildren={hasChildPages}
                                clearChildrenCustomText={props.clearConfirmationSettings.clearChildrenCustomText}
                                keepChildrenCustomText={props.clearConfirmationSettings.keepChildrenCustomText}
                                onClear={handleClearPage}
                                onClose={() => setSubmitClearModalOpened(false)}
                            />
                        )}
                        <ResolveOptionalPage
                            optional={props.optional ?? false}
                            unitId={props.unitId}
                            updateEmptyUnits={props.updateEmptyUnits}>
                            <IsFilledContext.Consumer>
                                {isFilled => (
                                    <>
                                        <UnitsLayoutItem
                                            minHeight={props.minHeight}
                                            maxWidth={isFilled ? props.maxWidth : DEFAULT_CONTENT_WIDTH_IN_PX}
                                            marginTop={marginTop}
                                            layoutKind={isFilled ? props.layoutKind : DEFAULT_CONTENT_LAYOUT}
                                            showBorder={props.showUnitBorder}
                                            ref={unitRef}
                                            onMouseEnter={handleSingleUnitMouseEnter}>
                                            {!loaded && !props.offPartialRender ? (
                                                <UnitLoader />
                                            ) : (
                                                renderSingleUnitContent(unitContent)
                                            )}
                                        </UnitsLayoutItem>
                                        {isFilled && nestedUnits}
                                    </>
                                )}
                            </IsFilledContext.Consumer>
                        </ResolveOptionalPage>
                    </UnitIdProvider>
                </AddSingleUnitContextProvider>
            </TestPageObjectHandle>
        </FocusManagementObserver>
    );
}
